import React from 'react';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { format, getMonth, getYear, setMonth, setYear } from 'date-fns';
import { Typography } from '../typography';
import { Button } from '@/components/ui/button';
import { Icon } from '../icon';
import Icons from '@/const/icons';

interface Props {
	title?: string;
	dateCurrent?: string;
	buttonContent?: string | JSX.Element;
	clearAll?: () => void;
	setValue: (value?: string) => void;
	endYearCustom?: number;
	startYearCustom?: number;
	disabled?: boolean;
	dateRange?: 'before-current' | 'after-current' | 'all';
	fromDate?: Date | undefined;
	toDate?: Date | undefined;
	classNameTrigger?: string;
}

export const DatePickerSimple: React.FC<Props> = ({
	title,
	buttonContent,
	setValue,
	clearAll,
	dateCurrent,
	endYearCustom,
	startYearCustom,
	disabled,
	dateRange = 'all',
	fromDate,
	toDate,
	classNameTrigger,
}) => {
	const [isOpen, setIsOpen] = React.useState(false);

	return (
		<Popover
			open={isOpen}
			onOpenChange={(open) => {
				if (!disabled) setIsOpen(open);
			}}
		>
			<PopoverTrigger disabled={disabled} className={classNameTrigger} asChild>
				<div
					// TEST: commeted because it was added in the popover component
					// onClick={() => {
					// 	if (!disabled) setIsOpen((prev) => !prev);
					// }}
					className="w-full relative"
				>
					{buttonContent}
				</div>
			</PopoverTrigger>

			<PopoverContent
				className="w-auto p-0 border-0 shadow-shadow2 pt-3 mt-4"
				align="start"
				sideOffset={-12}
				onPointerDownOutside={() => {
					setIsOpen(false);
				}}
			>
				{title && (
					<div className="px-2 pt-1">
						<Typography type="semibold-16">{title}</Typography>
					</div>
				)}

				<DatePickerCalendar
					endYearCustom={endYearCustom}
					isOpen={isOpen}
					setIsOpen={setIsOpen}
					setValue={setValue}
					startYearCustom={startYearCustom}
					clearAll={clearAll}
					dateCurrent={dateCurrent}
					dateRange={dateRange}
					disabled={disabled}
					fromDate={fromDate}
					toDate={toDate}
				/>
				{clearAll && (
					<Button
						variant={'ghost'}
						className="font-normal flex items-center gap-2 h-12"
						onClick={clearAll}
					>
						<Icon src={Icons.broom} className="!w-6 !h-6" />
						Clear All
					</Button>
				)}
			</PopoverContent>
		</Popover>
	);
};

interface DatePickerCalendarProps {
	dateCurrent?: string;
	clearAll?: () => void;
	setValue: (value?: string) => void;
	endYearCustom?: number;
	startYearCustom?: number;
	disabled?: boolean;
	dateRange?: 'before-current' | 'after-current' | 'all';
	fromDate?: Date | undefined;
	toDate?: Date | undefined;
	isOpen: boolean;
	setIsOpen?: (value: boolean) => void;
}

export const DatePickerCalendar: React.FC<DatePickerCalendarProps> = ({
	dateCurrent,
	isOpen,
	dateRange,
	fromDate,
	toDate,
	setValue,
	startYearCustom,
	endYearCustom,
	setIsOpen,
}) => {
	const [dateSelected, setDateSelected] = React.useState<Date | undefined>(
		dateCurrent ? new Date(dateCurrent) : undefined
	);
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();
	const yearSelected = dateSelected?.getFullYear();

	const [isMonthOpen, setIsMonthOpen] = React.useState(false);
	const [isYearOpen, setIsYearOpen] = React.useState(false);

	const startYear = startYearCustom || getYear(new Date()) - 100;
	const endYear = endYearCustom || getYear(new Date());

	//TODO Daniel Refactor this code in the future
	React.useEffect(() => {
		if (!isOpen) return;
		setIsMonthOpen(false);
		setIsYearOpen(false);
	}, [isOpen]);

	let years = Array.from(
		{ length: endYear - startYear + 1 },
		(_, i) => startYear + i
	);

	if (dateRange === 'before-current') {
		years = years.reverse();
	}

	const months = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	];

	const [monthSelected, setMonthSelected] = React.useState<Date | undefined>(
		dateCurrent ? new Date(dateCurrent) : undefined
	);

	const handleMonthChange = (month: string): void => {
		const newDate = setMonth(dateSelected || new Date(), months.indexOf(month));
		setMonthSelected(newDate);
		setDateSelected(newDate);

		// add this validation to avoid set invalid date
		const isDateValid =
			(!fromDate || newDate >= fromDate) && (!toDate || newDate <= toDate);
		if (isDateValid) {
			setValue(format(newDate, 'MM-dd-yyyy'));
		}
	};

	const handleYearChange = (year: string): void => {
		const newDate = setYear(monthSelected || new Date(), parseInt(year));
		setMonthSelected(newDate);
		setValue(format(newDate, 'MM-dd-yyyy'));
		setDateSelected(newDate);
	};

	const handleSelect = (dateSelectedCurrent: Date | undefined): void => {
		if (dateSelectedCurrent) {
			// const day = monthSelected
			// 	? setDate(monthSelected, dateSelectedCurrent.getDate())
			// 	: dateSelectedCurrent;

			// setValue(format(day, 'MM-dd-yyyy'));
			// setDateSelected(dateSelectedCurrent);
			// setIsOpen?.(false);

			// updated to this
			setValue(format(dateSelectedCurrent, 'MM-dd-yyyy'));
			setDateSelected(dateSelectedCurrent);
			setMonthSelected(dateSelectedCurrent);
			setIsOpen?.(false);
		}
	};

	return (
		<>
			<div className="flex justify-between gap-2 p-2">
				<Select
					value={monthSelected ? months[getMonth(monthSelected)] : undefined}
					open={isMonthOpen}
					onOpenChange={setIsMonthOpen}
					onValueChange={handleMonthChange}
				>
					<SelectTrigger className="flex-1 !text-purple-150">
						<SelectValue placeholder="Month" className="!text-purple-150" />
					</SelectTrigger>
					<SelectContent className="z-50 bg-white h-60">
						{months.map((month, index) => {
							const isDisabled =
								(dateRange === 'before-current' &&
									yearSelected === currentYear &&
									index > currentMonth) || // Disable future months in current year
								(dateRange === 'after-current' &&
									yearSelected === currentYear &&
									index < currentMonth); // Disable past months in current year
							return (
								<SelectItem
									key={month}
									disabled={isDisabled}
									value={month}
									className="pl-2"
								>
									{month}
								</SelectItem>
							);
						})}
					</SelectContent>
				</Select>
				<Select
					value={monthSelected ? getYear(monthSelected).toString() : undefined}
					open={isYearOpen}
					onOpenChange={setIsYearOpen}
					onValueChange={handleYearChange}
				>
					<SelectTrigger className="flex-1 !text-purple-150">
						<SelectValue placeholder="Year" className="!text-purple-150" />
					</SelectTrigger>
					<SelectContent className="z-50 bg-white h-60">
						{years.map((year) => (
							<SelectItem key={year} value={year.toString()} className="pl-2">
								{year}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<Calendar
				mode="single"
				selected={dateSelected}
				onSelect={handleSelect}
				classNames={{
					caption: 'hidden',
					nav_button: 'hidden',
					table: '!mt-0',
				}}
				initialFocus
				fromDate={fromDate}
				toDate={toDate}
				defaultMonth={dateSelected}
				month={dateSelected}
			/>
		</>
	);
};
